// App — top-level state machine. Three screens: Onboarding | Chat | Settings.
// Layout: sidebar (left) + main (center). Header strip at top of main when not onboarding.

import { useCallback, useEffect, useState } from 'react';
import * as api from './api';
import type { Brain, CompanyContext, Settings as SettingsT, Thread, ThreadAsset, AssetType } from './types';
import { Header } from './components/Header';
import { ThreadList } from './components/ThreadList';
import { AssetsPanel } from './components/AssetsPanel';
import { AssetModal } from './components/AssetModal';
import { Onboarding } from './screens/Onboarding';
import { Chat } from './screens/Chat';
import { Settings } from './screens/Settings';

type Screen = 'onboarding' | 'chat' | 'settings';

interface AppState {
  loading: boolean;
  loadError: string | null;
  screen: Screen;
  thread: Thread | null;
  threads: Thread[];
  settings: SettingsT | null;
  companyContext: CompanyContext | null;
  allBrains: Brain[];
  brainPillOpen: boolean;
  assetsRefreshKey: number;
  viewingAsset: ThreadAsset | null;
  assetGenerating: AssetType | null;
  assetError: string | null;
  // Mobile drawer state — only meaningful below the responsive breakpoints
  sidebarOpenMobile: boolean;
  assetsOpenMobile: boolean;
}

export function App() {
  const [state, setState] = useState<AppState>({
    loading: true,
    loadError: null,
    screen: 'chat',
    thread: null,
    threads: [],
    settings: null,
    companyContext: null,
    allBrains: [],
    brainPillOpen: false,
    assetsRefreshKey: 0,
    viewingAsset: null,
    assetGenerating: null,
    assetError: null,
    sidebarOpenMobile: false,
    assetsOpenMobile: false,
  });

  // Initial load
  useEffect(() => {
    (async () => {
      try {
        const [settings, companyContext, allBrains, threads] = await Promise.all([
          api.getSettings(),
          api.getCompanyContext(),
          api.listBrains(),
          api.listThreads(),
        ]);

        const needsOnboarding = !companyContext?.org_name;
        let thread: Thread | null = null;
        let updatedThreads = threads;
        let screen: Screen = needsOnboarding ? 'onboarding' : 'chat';
        if (!needsOnboarding) {
          // Always land on a fresh new-chat state. If the most recent thread
          // is already empty (untitled, no messages — e.g. from the last load),
          // reuse it instead of creating a duplicate.
          const reusableEmpty = threads.find((t) => !t.title);
          if (reusableEmpty) {
            thread = reusableEmpty;
          } else {
            thread = await api.createThread();
            updatedThreads = [thread, ...threads];
          }
        }

        setState({
          loading: false,
          loadError: null,
          screen,
          thread,
          threads: updatedThreads,
          settings,
          companyContext,
          allBrains,
          brainPillOpen: false,
          assetsRefreshKey: 0,
          viewingAsset: null,
          assetGenerating: null,
          assetError: null,
          sidebarOpenMobile: false,
          assetsOpenMobile: false,
        });
      } catch (err: any) {
        setState((s) => ({ ...s, loading: false, loadError: err?.message ?? String(err) }));
      }
    })();
  }, []);

  const handleOnboardingComplete = useCallback(async (savedContext: CompanyContext) => {
    const thread = await api.createThread();
    setState((s) => ({
      ...s,
      screen: 'chat',
      companyContext: savedContext,
      thread,
      threads: [thread, ...s.threads],
    }));
  }, []);

  const handleNavigate = useCallback((to: 'chat' | 'settings') => {
    setState((s) => ({ ...s, screen: to, brainPillOpen: false }));
  }, []);

  const handleBrainPanelChange = useCallback(async (newBrainIds: string[]) => {
    setState((s) => {
      if (!s.thread) return s;
      api.updateThreadBrainIds(s.thread.id, newBrainIds).then((updated) => {
        setState((s2) => ({
          ...s2,
          thread: updated,
          threads: s2.threads.map((t) => (t.id === updated.id ? updated : t)),
        }));
      });
      return s;
    });
  }, []);

  const handleThreadUpdate = useCallback((thread: Thread) => {
    setState((s) => ({
      ...s,
      thread: s.thread?.id === thread.id ? thread : s.thread,
      threads: s.threads.some((t) => t.id === thread.id)
        ? s.threads.map((t) => (t.id === thread.id ? thread : t))
        : [thread, ...s.threads],
    }));
  }, []);

  const handleCostBump = useCallback((cents: number) => {
    setState((s) =>
      s.settings
        ? { ...s, settings: { ...s.settings, daily_used_cents: s.settings.daily_used_cents + cents } }
        : s,
    );
  }, []);

  const handleNewChat = useCallback(async () => {
    const thread = await api.createThread();
    setState((s) => ({
      ...s,
      screen: 'chat',
      thread,
      threads: [thread, ...s.threads],
      brainPillOpen: false,
    }));
  }, []);

  const handleSelectThread = useCallback((id: string) => {
    setState((s) => ({
      ...s,
      screen: 'chat',
      thread: s.threads.find((t) => t.id === id) ?? s.thread,
      brainPillOpen: false,
      sidebarOpenMobile: false, // close drawer after pick
    }));
  }, []);

  const toggleSidebar = useCallback(() => {
    setState((s) => ({ ...s, sidebarOpenMobile: !s.sidebarOpenMobile, assetsOpenMobile: false }));
  }, []);
  const toggleAssets = useCallback(() => {
    setState((s) => ({ ...s, assetsOpenMobile: !s.assetsOpenMobile, sidebarOpenMobile: false }));
  }, []);
  const closeMobileDrawers = useCallback(() => {
    setState((s) => ({ ...s, sidebarOpenMobile: false, assetsOpenMobile: false }));
  }, []);

  const handleAssetGenerated = useCallback((asset: ThreadAsset) => {
    setState((s) => ({
      ...s,
      viewingAsset: asset,
      assetGenerating: null,
      assetError: null,
      assetsRefreshKey: s.assetsRefreshKey + 1,
    }));
  }, []);

  const handleAssetGeneratingChange = useCallback((type: AssetType | null) => {
    setState((s) => ({ ...s, assetGenerating: type, assetError: null, viewingAsset: null }));
  }, []);

  const handleAssetError = useCallback((msg: string) => {
    setState((s) => ({ ...s, assetError: msg, assetGenerating: null }));
  }, []);

  const handleAssetOpen = useCallback((asset: ThreadAsset) => {
    setState((s) => ({ ...s, viewingAsset: asset, assetGenerating: null, assetError: null }));
  }, []);

  const handleAssetModalClose = useCallback(() => {
    setState((s) => ({ ...s, viewingAsset: null, assetGenerating: null, assetError: null }));
  }, []);

  const handleThreadsChanged = useCallback((threads: Thread[]) => {
    setState((s) => {
      const current = s.thread;
      // If the active thread was deleted, fall back to the first remaining one or null
      const stillExists = current && threads.some((t) => t.id === current.id);
      let nextThread: Thread | null = current;
      if (!stillExists) {
        nextThread = threads[0] ?? null;
      }
      return { ...s, threads, thread: nextThread };
    });
  }, []);

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="font-mono text-sm text-text2">Loading Element IQ…</div>
      </div>
    );
  }

  if (state.loadError) {
    return (
      <div className="min-h-screen flex items-center justify-center px-8">
        <div className="max-w-md bg-card border border-accent/50 rounded-lg p-6">
          <div className="font-display text-[10px] text-accent uppercase tracking-wide14 mb-2">Could not reach Element IQ API</div>
          <div className="text-sm text-ink mb-4">{state.loadError}</div>
          <div className="text-xs text-text2 leading-relaxed">
            The backend at <code className="font-mono text-ink">element-iq-api.michael-4cf.workers.dev</code> is unreachable. Check the worker is deployed and the Anthropic API key secret is set.
          </div>
        </div>
      </div>
    );
  }

  if (state.screen === 'onboarding') {
    return (
      <Onboarding
        initialContext={state.companyContext}
        allBrains={state.allBrains}
        onComplete={handleOnboardingComplete}
      />
    );
  }

  if (!state.thread || !state.settings) {
    return <div className="min-h-screen flex items-center justify-center text-text2">No thread loaded.</div>;
  }

  // Org name (e.g., "ElementUSA") makes a friendlier greeting than the role
  // ("Process Engineer" → "Process" was a bug). If no org, drop the name.
  const greetingName = state.companyContext?.org_name?.trim() || undefined;

  const brainPillOpen = state.brainPillOpen && state.screen === 'chat';
  const setBrainPillOpen = (open: boolean) =>
    setState((s) => ({ ...s, brainPillOpen: open && s.screen === 'chat' }));

  return (
    <div className="flex flex-col min-h-screen">
      {/* TOP: Full-width header */}
      <Header
        currentScreen={state.screen}
        onNavigate={handleNavigate}
        activeThread={state.thread}
        onToggleSidebar={toggleSidebar}
        onToggleAssets={toggleAssets}
      />

      {/* Mobile backdrop — covers content when either drawer is open */}
      {(state.sidebarOpenMobile || state.assetsOpenMobile) && (
        <div
          onClick={closeMobileDrawers}
          className="fixed inset-0 bg-ink/30 z-20 md:hidden"
          aria-hidden="true"
        />
      )}

      {/* BELOW: Sidebar | Main | Assets rail */}
      <div className="flex flex-1 min-h-0">
        <ThreadList
          threads={state.threads}
          activeThreadId={state.thread?.id ?? null}
          onSelect={handleSelectThread}
          onNewChat={handleNewChat}
          onThreadsChanged={handleThreadsChanged}
          mobileOpen={state.sidebarOpenMobile}
        />

        <div className="flex-1 min-w-0 relative">
          {state.screen === 'chat' && (
            <Chat
              thread={state.thread}
              allBrains={state.allBrains}
              settings={state.settings}
              greetingName={greetingName}
              brainPillOpen={brainPillOpen}
              onBrainPillToggle={() => setBrainPillOpen(!brainPillOpen)}
              onBrainPanelChange={handleBrainPanelChange}
              onBrainPanelClose={() => setBrainPillOpen(false)}
              onCostBump={handleCostBump}
              onThreadUpdate={handleThreadUpdate}
              onAssetGenerated={handleAssetGenerated}
              onAssetGeneratingChange={handleAssetGeneratingChange}
              onAssetError={handleAssetError}
            />
          )}
          {state.screen === 'settings' && (
            <Settings
              settings={state.settings}
              companyContext={state.companyContext}
              allBrains={state.allBrains}
              onSettingsChange={(s) => setState((st) => ({ ...st, settings: s }))}
              onCompanyContextChange={(c) => setState((st) => ({ ...st, companyContext: c }))}
            />
          )}
        </div>

        <AssetsPanel
          activeThreadId={state.thread?.id ?? null}
          refreshKey={state.assetsRefreshKey}
          onOpen={handleAssetOpen}
          mobileOpen={state.assetsOpenMobile}
        />
      </div>

      <AssetModal
        asset={state.viewingAsset}
        generating={state.assetGenerating}
        error={state.assetError}
        onClose={handleAssetModalClose}
      />
    </div>
  );
}
